import { create } from 'zustand'
import type { Opportunity, OpportunityStage, KanbanColumn } from '@/types'

const STAGE_CONFIG: Record<OpportunityStage, { title: string; color: string }> = {
  qualified: { title: 'Qualificado', color: '#10b981' },
  proposal_sent: { title: 'Proposta Enviada', color: '#f59e0b' },
  negotiation: { title: 'Negociacao', color: '#f97316' },
  closing: { title: 'Fechamento', color: '#ef4444' },
  won: { title: 'Ganho', color: '#22c55e' },
  lost: { title: 'Perdido', color: '#6b7280' },
}

export const STAGE_ORDER: OpportunityStage[] = [
  'qualified',
  'proposal_sent',
  'negotiation',
  'closing',
  'won',
  'lost',
]

export { STAGE_CONFIG }

interface KanbanStore {
  columns: KanbanColumn[]
  selectedOpportunity: Opportunity | null
  isLoading: boolean
  error: string | null

  setOpportunities: (opportunities: Opportunity[]) => void
  moveOpportunity: (opportunityId: string, fromStage: OpportunityStage, toStage: OpportunityStage) => void
  selectOpportunity: (opportunity: Opportunity | null) => void
  updateOpportunity: (opportunityId: string, data: Partial<Opportunity>) => void
  addOpportunity: (opportunity: Opportunity) => void
  removeOpportunity: (opportunityId: string) => void
  setLoading: (isLoading: boolean) => void
  setError: (error: string | null) => void
}

const createEmptyColumns = (): KanbanColumn[] =>
  STAGE_ORDER.map((stage) => ({
    id: stage,
    title: STAGE_CONFIG[stage].title,
    color: STAGE_CONFIG[stage].color,
    opportunities: [],
    totalValue: 0,
    count: 0,
  }))

const calculateColumnStats = (opportunities: Opportunity[]): { totalValue: number; count: number } => ({
  totalValue: opportunities.reduce((sum, opp) => sum + opp.value, 0),
  count: opportunities.length,
})

export const useKanbanStore = create<KanbanStore>((set, get) => ({
  columns: createEmptyColumns(),
  selectedOpportunity: null,
  isLoading: false,
  error: null,

  setOpportunities: (opportunities) => {
    const columns = createEmptyColumns()
    opportunities.forEach((opp) => {
      const column = columns.find((col) => col.id === opp.stage)
      if (column) {
        column.opportunities.push(opp)
      }
    })
    columns.forEach((column) => {
      const stats = calculateColumnStats(column.opportunities)
      column.totalValue = stats.totalValue
      column.count = stats.count
    })
    set({ columns })
  },

  moveOpportunity: (opportunityId, fromStage, toStage) => {
    const { columns } = get()
    const newColumns = [...columns]
    const fromColumn = newColumns.find((col) => col.id === fromStage)
    const toColumn = newColumns.find((col) => col.id === toStage)
    if (!fromColumn || !toColumn) return
    const oppIndex = fromColumn.opportunities.findIndex((opp) => opp.id === opportunityId)
    if (oppIndex === -1) return
    const [opportunity] = fromColumn.opportunities.splice(oppIndex, 1)
    opportunity.stage = toStage
    opportunity.updated_at = new Date().toISOString()
    toColumn.opportunities.push(opportunity)
    const fromStats = calculateColumnStats(fromColumn.opportunities)
    fromColumn.totalValue = fromStats.totalValue
    fromColumn.count = fromStats.count
    const toStats = calculateColumnStats(toColumn.opportunities)
    toColumn.totalValue = toStats.totalValue
    toColumn.count = toStats.count
    set({ columns: newColumns })
  },

  selectOpportunity: (opportunity) => set({ selectedOpportunity: opportunity }),

  updateOpportunity: (opportunityId, data) => {
    const { columns, selectedOpportunity } = get()
    const newColumns = columns.map((column) => ({
      ...column,
      opportunities: column.opportunities.map((opp) =>
        opp.id === opportunityId ? { ...opp, ...data, updated_at: new Date().toISOString() } : opp
      ),
    }))
    newColumns.forEach((column) => {
      const stats = calculateColumnStats(column.opportunities)
      column.totalValue = stats.totalValue
      column.count = stats.count
    })
    set({
      columns: newColumns,
      selectedOpportunity: selectedOpportunity?.id === opportunityId ? { ...selectedOpportunity, ...data } : selectedOpportunity,
    })
  },

  addOpportunity: (opportunity) => {
    const { columns } = get()
    const newColumns = columns.map((column) => {
      if (column.id === opportunity.stage) {
        const newOpportunities = [...column.opportunities, opportunity]
        const stats = calculateColumnStats(newOpportunities)
        return { ...column, opportunities: newOpportunities, totalValue: stats.totalValue, count: stats.count }
      }
      return column
    })
    set({ columns: newColumns })
  },

  removeOpportunity: (opportunityId) => {
    const { columns, selectedOpportunity } = get()
    const newColumns = columns.map((column) => {
      const newOpportunities = column.opportunities.filter((opp) => opp.id !== opportunityId)
      const stats = calculateColumnStats(newOpportunities)
      return { ...column, opportunities: newOpportunities, totalValue: stats.totalValue, count: stats.count }
    })
    set({ columns: newColumns, selectedOpportunity: selectedOpportunity?.id === opportunityId ? null : selectedOpportunity })
  },

  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
}))
